import React, { useState, useEffect, useRef } from 'react';
import { SmartCaptcha } from '@yandex/smart-captcha';
import { useAuth } from '../contexts/AuthContext';
import {
  RUSSIA_REGIONS,
  getSubjects,
  getDistricts,
  getSettlements,
  getSubjectTypeName,
  type Subject,
  type District,
  type Settlement
} from '../../shared/data/russiaRegions';

interface AuthModalProps {
  mode: 'login' | 'register';
  onClose: () => void;
  onSwitchMode: () => void;
  onLoginSuccess?: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  background: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(212, 175, 55, 0.25)',
  borderRadius: '12px',
  color: '#fff',
  fontSize: '16px',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#d4af37',
  marginBottom: '6px',
  fontSize: '15px',
  fontWeight: 500,
};

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const goldButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
  color: '#0A0814',
  border: 'none',
  borderRadius: '12px',
  fontWeight: 'bold',
  cursor: 'pointer',
};

const AuthModal: React.FC<AuthModalProps> = ({ mode, onClose, onSwitchMode }) => {
  const { login, register } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    federalDistrict: '',
    subject: '',
    district: '',
    settlement: '',
    consent: false,
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Капча проходит ПЕРЕД формой регистрации
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [showCaptchaInline, setShowCaptchaInline] = useState(false);

  const siteKey = import.meta.env.VITE_SMARTCAPTCHA_SITE_KEY;

  const autoSubmitTriggered = useRef(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Автосабмит формы после капчи (Step 1 → Step 2)
  useEffect(() => {
    if (captchaVerified && formRef.current && !autoSubmitTriggered.current) {
      autoSubmitTriggered.current = true;
      setTimeout(() => formRef.current?.requestSubmit(), 150);
    }
  }, [captchaVerified]);

  const handleCaptchaSuccess = (token: string) => {
    console.log('[SmartCaptcha] ✓ Token received, length:', token.length);
    setCaptchaToken(token);
    setCaptchaVerified(true);
    setShowCaptchaInline(false);
    autoSubmitTriggered.current = false;
  };

  // Фоллбэк: если сложный челлендж (слайдер) не вызвал onSuccess,
  // проверяем токен при закрытии challenge
  const handleChallengeHidden = () => {
    console.log('[SmartCaptcha] Challenge hidden, checking for token...');
    const token = (window as any).smartCaptcha?.getResponse?.();
    if (token && !captchaVerified) {
      console.log('[SmartCaptcha] ✓ Token found via getResponse fallback, length:', token.length);
      setCaptchaToken(token);
      setCaptchaVerified(true);
      setShowCaptchaInline(false);
      autoSubmitTriggered.current = false;
    }
  };

  const handleCaptchaExpired = () => {
    console.warn('[SmartCaptcha] Token expired');
    setCaptchaToken(null);
    // Не показываем inline капчу сразу — только при сабмите
  };

  const handleCaptchaNetworkError = () => {
    setError('Сетевая ошибка капчи. Проверьте соединение.');
  };

  const handleCaptchaJsError = (error: unknown) => {
    console.error('[SmartCaptcha] Javascript error:', error);
    setError('Ошибка загрузки капчи. Обновите страницу.');
  };

  const getSubjectsList = (): Subject[] => {
    if (!formData.federalDistrict) return [];
    return getSubjects(formData.federalDistrict);
  };

  const getDistrictsList = (): District[] => {
    if (!formData.federalDistrict || !formData.subject) return [];
    return getDistricts(formData.federalDistrict, formData.subject);
  };

  const getSettlementsList = (): Settlement[] => {
    if (!formData.federalDistrict || !formData.subject || !formData.district) return [];
    return getSettlements(formData.federalDistrict, formData.subject, formData.district);
  };

  const isFormValid = (): boolean => {
    if (mode !== 'register') return true;
    return (
      formData.email !== '' &&
      formData.password !== '' &&
      formData.confirmPassword !== '' &&
      formData.fullName !== '' &&
      formData.birthDate !== '' &&
      formData.federalDistrict !== '' &&
      formData.subject !== '' &&
      formData.district !== '' &&
      formData.settlement !== '' &&
      formData.consent === true
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'register') {
      if (!captchaToken) {
        setShowCaptchaInline(true);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Пароли не совпадают');
        return;
      }

      setLoading(true);
      try {
        const birthPlace = `${formData.settlement}, ${formData.district}, ${formData.subject}, ${formData.federalDistrict}`;

        await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.fullName,
          birth_date: formData.birthDate,
          birth_time: formData.birthTime || undefined,
          birth_place: birthPlace,
          captchaToken: captchaToken,
        });

        setRegistrationSuccess(true);
      } catch (err: unknown) {
        if (err instanceof Error && 'errors' in (err as any)) {
          const zodErrors = (err as any).errors;
          if (Array.isArray(zodErrors) && zodErrors.length > 0) {
            setError(zodErrors.map((e: any) => e.message).join('. '));
          } else {
            setError(err.message);
          }
        } else {
          setError(err instanceof Error ? err.message : 'Произошла ошибка');
        }
      } finally {
        setLoading(false);
      }
    } else {
      // Login
      setLoading(true);
      try {
        await login(formData.email, formData.password);
        onClose();
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка');
      } finally {
        setLoading(false);
      }
    }
  };

  if (registrationSuccess) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, backdropFilter: 'blur(5px)',
      }}>
        <div style={{
          background: 'rgba(13, 20, 40, 0.85)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(212, 175, 55, 0.12)',
          borderRadius: '24px',
          boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
          padding: isMobile ? '30px 20px' : '50px 40px',
          maxWidth: '500px', width: '90%', textAlign: 'center',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
          <div style={{ position: 'absolute', inset: 0, borderRadius: '24px', background: 'linear-gradient(135deg, rgba(212,175,55,0.15), transparent 50%)', pointerEvents: 'none' }} />
          <div style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: '20px', position: 'relative', zIndex: 1 }}>✉️</div>
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 'bold',
            marginBottom: '20px',
            position: 'relative',
            zIndex: 1,
          }}>
            Регистрация успешна!
          </h2>
          <p style={{ color: '#b0b0b0', fontSize: isMobile ? '14px' : '16px', lineHeight: '1.8', marginBottom: '15px', position: 'relative', zIndex: 1 }}>
            На адрес <strong style={{ color: '#4fd1c5' }}>{formData.email}</strong> отправлено письмо для активации аккаунта.
          </p>
          <p style={{ color: '#b0b0b0', fontSize: isMobile ? '12px' : '14px', lineHeight: '1.6', marginBottom: '30px', position: 'relative', zIndex: 1 }}>
            Перейдите по ссылке в письме, чтобы подтвердить свою почту и получить доступ ко всем возможностям платформы.
          </p>
          <button
            onClick={onClose}
            style={{
              ...goldButtonStyle,
              padding: isMobile ? '12px 30px' : '14px 40px',
              fontSize: isMobile ? '14px' : '16px',
              position: 'relative', zIndex: 1,
            }}
          >
            Понятно
          </button>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: isMobile ? '12px' : '20px', right: isMobile ? '12px' : '20px',
              background: 'none', border: 'none', color: '#888',
              fontSize: isMobile ? '20px' : '24px', cursor: 'pointer',
              zIndex: 2,
            }}
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  const subjects = getSubjectsList();
  const districts = getDistrictsList();
  const settlements = getSettlementsList();

  // ШАГ 1: Капча (только для регистрации)
  if (mode === 'register' && !captchaVerified) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, backdropFilter: 'blur(5px)',
      }}>
        <div style={{
          background: 'rgba(13, 20, 40, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          borderRadius: '24px',
          boxShadow: '0 8px 40px rgba(212, 175, 55, 0.1)',
          padding: isMobile ? '24px 20px' : '36px',
          maxWidth: '420px', width: '90%',
          position: 'relative',
        }}>
          <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
          
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '22px' : '26px',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: '8px',
          }}>
            Регистрация
          </h2>
          <p style={{ color: '#c8c8c8', fontSize: '15px', textAlign: 'center', marginBottom: '24px' }}>
            Подтвердите, что вы не робот
          </p>

          {error && (
            <div style={{
              background: 'rgba(220, 53, 69, 0.2)',
              border: '1px solid #dc3545',
              color: '#ff6b6b',
              padding: '10px 14px',
              borderRadius: '12px',
              marginBottom: '16px',
              fontSize: '14px',
              textAlign: 'center',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {siteKey ? (
              <SmartCaptcha
                sitekey={siteKey}
                onSuccess={handleCaptchaSuccess}
                onChallengeHidden={handleChallengeHidden}
                onTokenExpired={handleCaptchaExpired}
                onNetworkError={handleCaptchaNetworkError}
                onJavascriptError={handleCaptchaJsError}
              />
            ) : (
              <div style={{ color: '#ff6b6b', fontSize: '14px' }}>
                Капча не настроена
              </div>
            )}
          </div>

          <div style={{ textAlign: 'center', marginTop: '20px', color: '#c8c8c8', fontSize: '14px' }}>
            Уже есть аккаунт?{' '}
            <button onClick={onSwitchMode} style={{
              background: 'none', border: 'none', color: '#4fd1c5',
              cursor: 'pointer', fontSize: '14px', textDecoration: 'underline',
            }}>
              Войти
            </button>
          </div>

          <button onClick={onClose} style={{
            position: 'absolute', top: '12px', right: '12px',
            background: 'none', border: 'none', color: '#888',
            fontSize: '22px', cursor: 'pointer',
          }}>
            ✕
          </button>
        </div>
      </div>
    );
  }

  // ШАГ 2: Форма регистрации (или форма входа)
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, backdropFilter: 'blur(5px)',
    }}>
      <div style={{
        background: 'rgba(13, 20, 40, 0.85)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.12)',
        borderRadius: '24px',
        boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
        padding: isMobile ? '20px 16px' : '32px',
        maxWidth: '520px', width: '90%',
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)', zIndex: 1 }} />
        <div style={{ position: 'absolute', inset: 0, borderRadius: '24px', background: 'linear-gradient(135deg, rgba(212,175,55,0.15), transparent 50%)', pointerEvents: 'none', zIndex: 0 }} />
        
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '16px' : '24px', position: 'relative', zIndex: 1 }}>
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '24px' : '30px',
            fontWeight: 'bold',
            marginBottom: '8px',
          }}>
            {mode === 'register' ? 'Заполните данные' : 'Вход'}
          </h2>
          <p style={{ color: '#c8c8c8', fontSize: isMobile ? '14px' : '15px' }}>
            {mode === 'register' ? 'Создайте аккаунт Astranum' : 'Войдите в свой аккаунт'}
          </p>
          {mode === 'register' && captchaVerified && captchaToken && (
            <p style={{ color: '#4fd1c5', fontSize: '13px', marginTop: '6px' }}>
              ✓ Капча пройдена
            </p>
          )}
        </div>

        {error && (
          <div style={{
            background: 'rgba(220, 53, 69, 0.2)',
            border: '1px solid #dc3545',
            color: '#ff6b6b',
            padding: '10px 14px',
            borderRadius: '12px',
            marginBottom: '16px',
            fontSize: '14px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}>
            {error}
          </div>
        )}

        <form ref={formRef} onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="example@mail.ru"
              style={inputStyle}
            />
          </div>

          {mode === 'register' && (
            <>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Полное имя</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  placeholder="Иван Иванов"
                  style={inputStyle}
                />
              </div>

              {/* Дата и время рождения в одну строку */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                <div style={{ flex: 2 }}>
                  <label style={labelStyle}>Дата рождения</label>
                  <input
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                    required
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Время</label>
                  <input
                    type="time"
                    value={formData.birthTime}
                    onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                    placeholder="12:00"
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Место рождения — компактные селекты */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Федеральный округ</label>
                  <select
                    value={formData.federalDistrict}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        federalDistrict: e.target.value,
                        subject: '',
                        district: '',
                        settlement: ''
                      });
                    }}
                    required
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    <option value="" style={{ background: '#1a1a3e' }}>Округ</option>
                    {RUSSIA_REGIONS.map((fd) => (
                      <option key={fd.name} value={fd.name} style={{ background: '#1a1a3e' }}>
                        {fd.name}
                      </option>
                    ))}
                  </select>
                </div>
                {formData.federalDistrict && (
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Регион</label>
                    <select
                      value={formData.subject}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          subject: e.target.value,
                          district: '',
                          settlement: ''
                        });
                      }}
                      required
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="" style={{ background: '#1a1a3e' }}>Регион</option>
                      {subjects.map((subject) => (
                        <option key={subject.name} value={subject.name} style={{ background: '#1a1a3e' }}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {formData.subject && (
                <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Район</label>
                    <select
                      value={formData.district}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          district: e.target.value,
                          settlement: ''
                        });
                      }}
                      required
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="" style={{ background: '#1a1a3e' }}>Район</option>
                      {districts.map((district) => (
                        <option key={district.name} value={district.name} style={{ background: '#1a1a3e' }}>
                          {district.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {formData.district && (
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Город / Поселение</label>
                      <select
                        value={formData.settlement}
                        onChange={(e) => setFormData({ ...formData, settlement: e.target.value })}
                        required
                        style={{ ...inputStyle, cursor: 'pointer' }}
                      >
                        <option value="" style={{ background: '#1a1a3e' }}>Населённый пункт</option>
                        {settlements.map((settlement) => (
                          <option key={settlement.name} value={settlement.name} style={{ background: '#1a1a3e' }}>
                            {settlement.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Пароль</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                placeholder="Минимум 8 символов"
                style={inputStyle}
              />
            </div>
            {mode === 'register' && (
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Повторите пароль</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  placeholder="Повторите"
                  style={inputStyle}
                />
              </div>
            )}
          </div>

          {mode === 'login' && (
            <div style={{ textAlign: 'right', marginBottom: '14px', marginTop: '-6px' }}>
              <a href="/forgot-password" style={{ color: '#4fd1c5', fontSize: '13px', textDecoration: 'none' }}>
                Забыли пароль?
              </a>
            </div>
          )}

          {mode === 'register' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.consent}
                  onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                  required
                  style={{ marginTop: '3px', width: '18px', height: '18px', flexShrink: 0 }}
                />
                <span style={{ color: '#d0d0d0', fontSize: '14px', lineHeight: '1.4' }}>
                  Согласие на обработку персональных данных в соответствии с{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#4fd1c5' }}>
                    Политикой конфиденциальности
                  </a>
                </span>
              </label>
            </div>
          )}

          {mode === 'register' && showCaptchaInline && (
            <div style={{ marginBottom: '16px', padding: '16px', background: 'rgba(212, 175, 55, 0.05)', borderRadius: '12px', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
              <p style={{ color: '#d4af37', fontSize: '14px', marginBottom: '12px', textAlign: 'center' }}>
                Подтвердите, что вы не робот
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {siteKey ? (
                  <SmartCaptcha
                    sitekey={siteKey}
                    onSuccess={handleCaptchaSuccess}
                    onChallengeHidden={handleChallengeHidden}
                    onTokenExpired={handleCaptchaExpired}
                    onNetworkError={handleCaptchaNetworkError}
                    onJavascriptError={handleCaptchaJsError}
                  />
                ) : (
                  <div style={{ color: '#ff6b6b', fontSize: '14px' }}>
                    Капча не настроена
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (mode === 'register' && !isFormValid())}
            style={{
              ...goldButtonStyle,
              width: '100%',
              padding: isMobile ? '13px' : '15px',
              background: (loading || (mode === 'register' && !isFormValid())) ? '#666' : goldButtonStyle.background,
              fontSize: isMobile ? '16px' : '18px',
              cursor: (loading || (mode === 'register' && !isFormValid())) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              marginBottom: '16px',
              opacity: (mode === 'register' && !isFormValid()) ? 0.6 : 1,
            }}
          >
            {loading ? 'Загрузка...' : mode === 'register' ? 'Зарегистрироваться' : 'Войти'}
          </button>
        </form>

        <div style={{ textAlign: 'center', color: '#c8c8c8', fontSize: isMobile ? '14px' : '15px', position: 'relative', zIndex: 1 }}>
          {mode === 'register' ? 'Уже есть аккаунт?' : 'Нет аккаунта?'}{' '}
          <button onClick={onSwitchMode} style={{
            background: 'none', border: 'none', color: '#4fd1c5',
            cursor: 'pointer', fontSize: isMobile ? '14px' : '15px', textDecoration: 'underline',
          }}>
            {mode === 'register' ? 'Войти' : 'Зарегистрироваться'}
          </button>
        </div>

        <button onClick={onClose} style={{
          position: 'absolute', top: isMobile ? '12px' : '16px', right: isMobile ? '12px' : '16px',
          background: 'none', border: 'none', color: '#888',
          fontSize: isMobile ? '20px' : '24px', cursor: 'pointer',
          zIndex: 2,
        }}>
          ✕
        </button>
      </div>
    </div>
  );
};

export default AuthModal;
